import {DataStatus, FormControl, FormField, getApiYearOptions} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {reverse, uniq} from 'ramda';

import {useGetStageSequenceMandatoryFieldsListQuery} from '@dms/api/settings';
import {VinDecoderResponseBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {
  hasSecondaryFuelType,
  MakeSelect,
  mandatoryFieldsMap,
  ModelSelect,
  SecondaryFuelTypeField,
  useVehicleCatalogue,
  VinDecoderField,
} from '@dms/shared';

import {
  buildObject,
  getApiDateString,
  handleFormReset,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {BuyingFormType} from '../types/BuyingFormType';

interface BuyingVehicleFormProps extends RequiredTestIdProps {
  control: FormControl<BuyingFormType>;
  formApi: UseFormReturn<BuyingFormType>;
  onSave?: (values: BuyingFormType) => void;
}

export function BuyingVehicleForm(props: BuyingVehicleFormProps) {
  const [vehicleTypes, make, fuelTypes] = useWatch({
    control: props.control,
    name: ['vehicleData.type', 'vehicleData.make', 'vehicleData.fuelType'],
  });

  const vehicleType = vehicleTypes?.[0] || 'VEHICLETYPE_PASSENGER_CAR';
  const fuelType = fuelTypes?.[0];

  const [vehicleUtils, {isLoading: isVehicleCatalogLoading, isError}] = useVehicleCatalogue(
    vehicleType,
    false
  );

  const {data: mandatorySS, isLoading: isMandatoryFieldsLoading} =
    useGetStageSequenceMandatoryFieldsListQuery({
      definitionId: 'SALES',
    });

  const mandatoryFields =
    mandatorySS?.stageSequences?.flatMap((ss) => ss.mandatoryFields.map((f) => f.fieldId)) ?? [];

  const mandatoryFieldsUniq = uniq(
    mandatoryFields
      .map((field) => mandatoryFieldsMap[field as keyof typeof mandatoryFieldsMap]?.formField ?? '')
      .filter(Boolean)
  );

  const isLoading = isVehicleCatalogLoading || isMandatoryFieldsLoading;

  const isMandatoryField = (field: string) => mandatoryFieldsUniq.includes(field);

  const handleDecodeVin =
    (formApi: UseFormReturn<BuyingFormType>) => (decodedData?: VinDecoderResponseBody) => {
      if (!decodedData) {
        return;
      }
      const currentValues = formApi.getValues();

      let firstRegistrationDate: string | undefined;
      if (
        decodedData.firstRegistrationOnYear &&
        decodedData.firstRegistrationOnMonth &&
        decodedData.firstRegistrationOnDay
      ) {
        try {
          const year = parseInt(decodedData.firstRegistrationOnYear, 10);
          // Month from API is 1-based, Date constructor needs 0-based
          const month = parseInt(decodedData.firstRegistrationOnMonth, 10);
          const day = parseInt(decodedData.firstRegistrationOnDay, 10);
          if (!isNaN(year) && !isNaN(month) && !isNaN(day) && month >= 1 && month <= 12) {
            firstRegistrationDate = getApiDateString(new Date(year, month - 1, day)); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
          }
        } catch (error) {
          console.error('Error parsing first registration date:', error);
        }
      }

      const power = decodedData.power ? parseInt(decodedData.power, 10) : undefined;
      const engineVolume = decodedData.engineVolume
        ? parseInt(decodedData.engineVolume, 10)
        : undefined;
      const maximalPower = decodedData.maximalPower
        ? parseInt(decodedData.maximalPower, 10)
        : undefined;

      const newVehicleData = buildObject<Partial<BuyingFormType['vehicleData']>>(
        currentValues.vehicleData
      )
        .type(
          decodedData.vehicleType ? [decodedData.vehicleType] : undefined,
          !!decodedData.vehicleType
        )
        .make(decodedData.make ?? undefined, !!decodedData.make)
        .modelFamily(decodedData.modelFamily ?? undefined, !!decodedData.modelFamily)
        .trim(decodedData.trim ?? undefined, !!decodedData.trim)
        .variant(decodedData.variant ?? undefined, !!decodedData.variant)
        .realProductionYear(
          decodedData.manufacturedOnYear ?? undefined,
          !!decodedData.manufacturedOnYear
        )
        .firstRegistration(firstRegistrationDate, !!firstRegistrationDate)
        .note(decodedData.otherRecords ?? undefined, !!decodedData.otherRecords)
        .bodyStyle(
          decodedData.bodyStyle ? [decodedData.bodyStyle] : undefined,
          !!decodedData.bodyStyle
        )
        .fuelType(decodedData.fuelType ? [decodedData.fuelType] : undefined, !!decodedData.fuelType)
        .secondaryFuelType(
          decodedData.secondaryFuelType ? [decodedData.secondaryFuelType] : undefined,
          !!decodedData.secondaryFuelType
        )
        .transmission(
          decodedData.transmission ? [decodedData.transmission] : undefined,
          !!decodedData.transmission
        )
        .drive(decodedData.drive ? [decodedData.drive] : undefined, !!decodedData.drive)
        .power(power, !isNaN(power ?? NaN))
        .engineVolume(engineVolume, !isNaN(engineVolume ?? NaN))
        .maximalPower(maximalPower, !isNaN(maximalPower ?? NaN))
        .features(decodedData.features ?? undefined, !!decodedData.features)
        .build();

      const newFormValues: BuyingFormType = {
        ...currentValues,
        vehicleData: newVehicleData as BuyingFormType['vehicleData'],
      };

      formApi.reset(newFormValues);
      props.onSave?.(newFormValues);
      props.control.onChange?.();
    };

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
      <VStack spacing={4}>
        <VinDecoderField<BuyingFormType>
          control={props.control}
          formApi={props.formApi}
          registrationPlateField={{
            name: 'vehicleData.licencePlate',
            label: i18n.t('entity.vehicle.labels.licensePlate'),
            isRecommended: isMandatoryField('registrationPlate'),
          }}
          vinField={{
            name: 'vehicleData.vin',
            label: i18n.t('entity.vehicle.labels.vin'),
            isRecommended: isMandatoryField('vin'),
          }}
          isCebiaFeatureDecoderEnabled
          onDecodedData={handleDecodeVin(props.formApi)}
          data-testid={suffixTestId('vinDecoder', props)}
        />
        <FormField
          control={props.control}
          type="chips"
          name="vehicleData.type"
          options={vehicleUtils.vehicleTypeOptions ?? []}
          label={i18n.t('entity.vehicle.labels.vehicleType')}
          isRequired
          onChange={() =>
            handleFormReset<BuyingFormType>(props.formApi, [
              {name: 'vehicleData.make', value: null},
              {name: 'vehicleData.modelFamily', value: null},
              {name: 'vehicleData.trim', value: null},
              {name: 'vehicleData.variant', value: null},
              {name: 'vehicleData.bodyStyle', value: null},
              {name: 'vehicleData.transmission', value: null},
              {name: 'vehicleData.drive', value: null},
              {name: 'vehicleData.fuelType', value: null},
              {name: 'vehicleData.secondaryFuelType', value: null},
              {name: 'vehicleData.power', value: null},
              {name: 'vehicleData.maximalPower', value: null},
              {name: 'vehicleData.engineVolume', value: null},
            ])
          }
          data-testid={suffixTestId('buyingVehicleForm-vehicleType', props)}
        />
        <Grid columns={[2, 4]}>
          <MakeSelect<BuyingFormType>
            vehicleType={vehicleType}
            control={props.control}
            isRequired
            name="vehicleData.make"
            label={i18n.t('entity.vehicle.labels.make')}
            data-testid={suffixTestId('serviceVehicleForm', props)}
            onChange={() =>
              handleFormReset<BuyingFormType>(props.formApi, [
                {name: 'vehicleData.modelFamily', value: null},
                {name: 'vehicleData.trim', value: null},
                {name: 'vehicleData.variant', value: null},
                {name: 'vehicleData.power', value: null},
                {name: 'vehicleData.maximalPower', value: null},
                {name: 'vehicleData.engineVolume', value: null},
              ])
            }
          />
          <ModelSelect<BuyingFormType>
            vehicleType={vehicleType}
            makes={make}
            control={props.control}
            isRequired
            name="vehicleData.modelFamily"
            label={i18n.t('entity.vehicle.labels.model')}
            data-testid={suffixTestId('serviceVehicleForm', props)}
          />
          <FormField
            control={props.control}
            type="text"
            name="vehicleData.trim"
            label={i18n.t('entity.vehicle.labels.trimLevel')}
            data-testid={suffixTestId('buyingVehicleForm-trim', props)}
            isRecommended={isMandatoryField('trim')}
          />
          <FormField
            control={props.control}
            type="text"
            name="vehicleData.variant"
            label={i18n.t('entity.vehicle.labels.variantName')}
            data-testid={suffixTestId('buyingVehicleForm-variant', props)}
            isRecommended={isMandatoryField('variant')}
          />
        </Grid>
        <Grid columns={[2, 4]}>
          <FormField
            control={props.control}
            type="choice"
            name="vehicleData.realProductionYear"
            options={reverse(getApiYearOptions(null, null))}
            label={i18n.t('entity.vehicle.labels.realProductionYear')}
            data-testid={suffixTestId('buyingVehicleForm-realProductionYear', props)}
            isRecommended={isMandatoryField('manufacturedOnYear')}
          />
          <FormField
            control={props.control}
            type="apiDate"
            name="vehicleData.firstRegistration"
            label={i18n.t('entity.vehicle.labels.firstRegistration')}
            data-testid={suffixTestId('buyingVehicleForm-firstRegistrationOn', props)}
            isRecommended={isMandatoryField('firstRegistrationOn')}
          />
          <FormField
            control={props.control}
            type="integer"
            name="vehicleData.mileage"
            label={i18n.t('entity.vehicle.labels.mileage')}
            suffix={i18n.t('general.metric.km')}
            data-testid={suffixTestId('buyingVehicleForm-mileage', props)}
            isRecommended={isMandatoryField('mileage')}
          />
        </Grid>
        <Grid columns={[2, 4]}>
          <FormField
            control={props.control}
            type="integer"
            name="vehicleData.power"
            suffix={i18n.t('general.metric.kW')}
            label={i18n.t('entity.vehicle.labels.power')}
            data-testid={suffixTestId('buyingVehicleForm-power', props)}
            isRecommended={isMandatoryField('power')}
          />
          <FormField
            control={props.control}
            type="integer"
            name="vehicleData.maximalPower"
            label={i18n.t(`entity.vehicle.labels.maximalPower`)}
            suffix={i18n.t('general.metric.kW')}
            isRecommended={isMandatoryField('maximalPower')}
          />
          <FormField
            control={props.control}
            type="integer"
            name="vehicleData.engineVolume"
            suffix={i18n.t('general.metric.ccm')}
            label={i18n.t('entity.vehicle.labels.engineCapacity')}
            data-testid={suffixTestId('buyingVehicleForm-engineCapacity', props)}
            isRecommended={isMandatoryField('engineVolume')}
          />
        </Grid>
        <FormField
          control={props.control}
          type="chips"
          name="vehicleData.bodyStyle"
          options={vehicleUtils.vehicleStyleOptions ?? []}
          label={i18n.t('entity.vehicle.labels.body')}
          data-testid={suffixTestId('buyingVehicleForm-bodyStyle', props)}
          isRecommended={isMandatoryField('bodyStyle')}
        />
        <FormField
          control={props.control}
          type="chips"
          name="vehicleData.fuelType"
          options={vehicleUtils.fuelTypeOptions ?? []}
          label={i18n.t('entity.vehicle.labels.fuel')}
          data-testid={suffixTestId('buyingVehicleForm-fuelType', props)}
          isRecommended={isMandatoryField('fuelType')}
          onChange={() =>
            handleFormReset<BuyingFormType>(props.formApi, [
              {name: 'vehicleData.secondaryFuelType', value: null},
              // {name: 'vehicleData.power', value: null},
              // {name: 'vehicleData.engineVolume', value: null},
            ])
          }
        />
        <Show when={hasSecondaryFuelType(fuelType)}>
          <SecondaryFuelTypeField<BuyingFormType>
            fuelType={fuelType}
            vehicleType={vehicleType}
            control={props.control}
            name="vehicleData.secondaryFuelType"
            label={i18n.t('entity.vehicle.labels.secondaryFuelType')}
            data-testid={suffixTestId('buyingVehicleForm', props)}
            isDeselectable
          />
        </Show>
        <Grid columns={[1, 2]}>
          <FormField
            control={props.control}
            type="chips"
            name="vehicleData.transmission"
            options={vehicleUtils.transmissionOptions ?? []}
            label={i18n.t('entity.vehicle.labels.transmission')}
            data-testid={suffixTestId('buyingVehicleForm-transmission', props)}
            isRecommended={isMandatoryField('transmission')}
          />
          <FormField
            control={props.control}
            type="chips"
            name="vehicleData.drive"
            options={vehicleUtils.driveOptions ?? []}
            label={i18n.t('entity.vehicle.labels.drive')}
            data-testid={suffixTestId('buyingVehicleForm-drive', props)}
            isRecommended={isMandatoryField('drive')}
          />
        </Grid>
        <FormField
          control={props.control}
          type="textarea"
          name="vehicleData.note"
          label={i18n.t('entity.vehicle.labels.additionalInformation')}
          data-testid={suffixTestId('buyingVehicleForm-note', props)}
        />
      </VStack>
    </DataStatus>
  );
}
